import { useStates } from '@tiger/hooks';
import { Fragment, useEffect } from 'react';
import { FormControl, FormLabel } from 'react-bootstrap';
import { $Formutil, connect, Form } from 'react-bootstrap-formutil';
import Button from 'components/base/Button';
import Modal from 'components/base/Modal';
import FormGroup from 'components/FormGroup';
import Toast from 'components/Toast';
import { Collapse } from 'components/Transition';
import useCountdown from 'hooks/useCountDown';
import { EventParams } from 'types/common';
import showError from 'utils/form/showFieldError';
import { printf } from 'utils/i18n';
import { useTrack } from 'utils/track';
import GeeCaptcha from '../Captcha';
import { sendSMS } from './request';
import './style.scss';

// wiki https://wiki.tigerbrokers.net/pages/viewpage.action?pageId=20615776 @江帅
type ProcessTypes =
    | 'signup'
    | 'fast_signin'
    | 'modify_email'
    | 'modify_phone'
    | 'modify_login_password'
    | 'modify_trade_password'
    | 'reset_login_password'
    | 'reset_trade_password'
    | 'bind_email'
    | 'bind_phone'
    | 'account_manage'
    | 'internal_funds_transfer'
    | 'internal_position_transfer'
    | 'personal_withdraw'
    | 'withdrawal'
    | 'fund'
    | 'retrieve_ib_password'
    | 'bank_card_application'
    | 'verify_code_signin'; // 验证码注册

export interface VerifyCodeSendProps {
    platform?: 'portal' | 'oauth';
    type?: 'phone' | 'email'; // 验证账号类型，手机号或者邮箱
    name?: string;
    username?: string; // 账号，需要传递对应 type 的类型的账号值，如果是手机号的话，还需要额外传递telcode。登录状态下可以传递username={null}，则为给当前登录用户的绑定账号发送
    logined?: boolean; // 是否登录模式，如果传true，调用发验证码接口时将不会传递phone或者email，默认给当前登录用户发送
    process: ProcessTypes; // 短信用途
    tel_code?: string;
    timeout?: number;
    label?: React.ReactNode;
    disabled?: boolean;
    onSend?(): void;
    $formutil: $Formutil<any>;
    eventParams?: {
        sendVerifyCode?: EventParams;
        inputVerifyCode?: EventParams;
    };
}

export interface VerifyCodeSendState {
    showVoice: boolean;
    errorLevel: 0 | 1 | 'off' | 2 | undefined;
}

const $validators = { isError: () => true };
const VerifyCodeSend: React.FC<VerifyCodeSendProps> = ({
    timeout = 60,
    platform = 'oauth',
    type = 'phone',
    name = 'verify_code',
    tel_code = '86',
    process,
    logined,
    username,
    onSend,
    $formutil,
    disabled: propsDisabled,
    label,
    eventParams
}) => {
    const [{ showVoice, errorLevel }, setState] = useStates<VerifyCodeSendState, {}>({
        showVoice: false,
        errorLevel: 1
    });
    const { countdown, start, isCounting, isEnd } = useCountdown(timeout || 60);
    const { sendVerifyCode: sendVerifyCodeEventParams, inputVerifyCode: inputVerifyCodeEventParams } =
        eventParams || {};
    const { trackEvent, debounceTrackEvent } = useTrack();

    const sendMsg = () => {
        createVerify();
    };

    const sendVoiceMsg = () => {
        createVerify(true);
    };

    const createVerify = (voice = false) => {
        if (isCounting) {
            return;
        }

        if (sendVerifyCodeEventParams) {
            trackEvent(sendVerifyCodeEventParams);
        }

        Modal.open({
            backdrop: 'static',
            component: props => (
                <Form<any>
                    children={$formutil => {
                        return (
                            <div className="modal-sendmsg-verify">
                                <h3 className="title">{__('Identity verification')}</h3>
                                <div className="form-group">
                                    <GeeCaptcha
                                        isVoice={voice}
                                        onError={() => props.dismiss()}
                                        onSuccess={() => {
                                            const { $params } = $formutil.$new();

                                            props.close($params);
                                        }}
                                        action={process}
                                    />
                                </div>
                                <footer>
                                    <Button type="light" className="btn btn-default" onClick={() => props.dismiss()}>
                                        {__('cancel')}
                                    </Button>
                                    <Button
                                        className={'btn btn-primary'}
                                        onClick={() => {
                                            const { $invalid, $getFirstError, $params } = $formutil;

                                            if ($invalid) {
                                                Toast.show($getFirstError());
                                            } else {
                                                props.close($params);
                                            }
                                        }}>
                                        {__('submit')}
                                    </Button>
                                </footer>
                            </div>
                        );
                    }}
                />
            )
        })
            .result.then(params => sendRequest(params, voice))
            .catch(() => {});
    };

    const sendRequest = async ($params, voice = false) => {
        Toast.loading(true, __('Sending...'));

        try {
            const params = {
                process,
                tel_code,
                voice: voice ? '1' : '0', // oauth方式
                type: voice ? 'voice' : '', // portal 方式

                ...$params
            };

            if (!logined) {
                params[type] = username;
            }

            await sendSMS(params, platform, type);

            start();

            onSend?.();
        } catch (error) {
            handleError(error);
        }

        Toast.loading(false);
    };

    useEffect(() => {
        if (isEnd) {
            setState({
                showVoice: true
            });
        }
    }, [isEnd, setState]);

    // 根据不同的error_code显示不同的错误提示信息
    const handleError = error => {
        const usernameFieldUtil = $formutil.$getField('username');
        const verifyCodeFieldUtil = $formutil.$getField(name as any);
        let errMsg = error.error_msg;

        switch (Number(error.error_code)) {
            case 11081:
                errMsg =
                    type === 'email'
                        ? __('E-mail has already been taken')
                        : __('This phone number has already been used');

                showError(usernameFieldUtil, errMsg);
                break;
            case 11071:
                errMsg = __('Invalid phone number');
                showError(usernameFieldUtil, errMsg);
                break;
            case 11042:
                errMsg = __('You have requested too many codes recently. Please wait 24 hours and try again');

                if (!verifyCodeFieldUtil) {
                    Toast.show(errMsg);
                    break;
                }

                verifyCodeFieldUtil?.$setError(
                    {
                        isError: errMsg
                    },
                    () => {
                        setState({
                            errorLevel: 2
                        });
                    }
                );

                break;
            default:
                Toast.show(errMsg);
                break;
        }
    };

    const hasError = username === '';
    const disabled = hasError || isCounting || propsDisabled;
    const labelNode = typeof label === 'string' ? <FormLabel>{label}</FormLabel> : label;

    return (
        <Fragment>
            {labelNode}
            <div className="verify-code-root">
                <FormGroup
                    name={name}
                    isError
                    required
                    $validators={$validators}
                    validMessage={{
                        required: __('Please enter the verification code')
                    }}
                    errorLevel={errorLevel}
                    $onFieldChange={() => {
                        if (inputVerifyCodeEventParams) {
                            debounceTrackEvent(inputVerifyCodeEventParams);
                        }
                    }}>
                    <FormControl placeholder={__('Verification Code')} />
                </FormGroup>
                <Button type="link" className="send-code-btn" disabled={disabled} onClick={sendMsg}>
                    {isCounting ? printf(__('Resend after %ss'), countdown) : __('Get verification code')}
                </Button>
            </div>
            {/* 邮箱验证不需要语音验证 */}
            <Collapse in={showVoice && type === 'phone'}>
                <div className="form-group">
                    <Button
                        type="dark"
                        ghost
                        block
                        disabled={disabled}
                        onClick={sendVoiceMsg}
                        className="send-voice-btn">
                        {isCounting ? printf(__('Resend after %ss'), countdown) : __('Send a voice verification code.')}
                    </Button>
                </div>
            </Collapse>
        </Fragment>
    );
};

export default connect(VerifyCodeSend);
